import React from "react";
import { Drawer, Chip, Stack, Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";

import { FormInputText } from "../ui/form/TextInput";
import { FormInputDropdown } from "../ui/form/Select";
import { CommunityReviewRecommendationSection } from "./RecommendationSection";
import Button from "@mui/material/Button";
import { useUpdateCommunityReview } from "./hooks/useCommunityReviewActions";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { STATUS_OPTIONS } from "../../constants/communityReview";

const chipLabels = [
  "Quality",
  "Value",
  "Durability",
  "Recycling & Resale",
  "Warranty & Repairs"
];

const validationSchema = yup.object({
  tags: yup
    .array()
    .of(yup.string())
    .required("Tags are required"),
  isRecommended: yup
    .boolean()
    .required("Recommendation status is required"),
  content: yup
    .string()
    .required("Content is required"),
  status: yup
    .string()
    .oneOf(STATUS_OPTIONS.map((option) => option.value), "Invalid status")
    .required("Status is required")
}).required();

export const CommunityReviewDrawer = ({ review, isOpen, onClose }) => {
  const { updateCommunityReview } = useUpdateCommunityReview();

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: {
      ...review
    },
    resolver: yupResolver(validationSchema)
  });

  const currentTags = watch("tags");
  const isRecommended = watch("isRecommended");

  const handleTagClick = (tag) => {
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];

    setValue("tags", newTags, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleRecommendationClick = (recommendation) => {
    setValue("isRecommended", recommendation);
  };

  const onSubmit = async (data) => {
    const { tags, isRecommended, content, status } = data;

    await updateCommunityReview.mutateAsync({
      id: review.id,
      data: {
        tags,
        isRecommended,
        content,
        status
      }
    });

    onClose?.();
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: 800,
          padding: "24px 32px",
          paddingBottom: "0",
          overflow: "auto"
        }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%" }}>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          height: "100%"
        }}>

          <Box sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "100%"
          }}>
            <Typography variant="h2" gutterBottom sx={{
              fontWeight: 500
            }}>
              {review.brandName}
            </Typography>
          </Box>

          <Box>
            <Typography variant="h4" gutterBottom>
              Will you recommend {review.brandName}?
            </Typography>
            <CommunityReviewRecommendationSection isRecommended={isRecommended} onClick={handleRecommendationClick} />
          </Box>

          <Box sx={{
            my: "2rem"
          }}>
            <Typography variant="h4" gutterBottom>
              Review Tags
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Select all that apply
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              {
                chipLabels.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    onClick={() => handleTagClick(label)}
                    variant={currentTags.includes(label) ? "filled" : "outlined"}
                    color="secondary"
                  />
                ))
              }
            </Stack>
          </Box>

          <FormInputText name="content" control={control} label="Tell us more" multiline />

          <FormInputDropdown
            name="status"
            control={control}
            label="Status"
            options={STATUS_OPTIONS}
          />

          {
            review.reviewModeration && (
              <>
                <Typography variant="h4" sx={{
                  fontWeight: 500
                }}>
                  Review Moderation
                </Typography>
                <Box backgroundColor="#f5f5f5" padding="16px" borderRadius="8px">
                  <pre>
                    <code style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {JSON.stringify(review.reviewModeration, null, 2)}
                    </code>
                  </pre>
                </Box>
              </>
            )
          }

          <Box sx={{
            mt: "auto",
            display: "flex",
            justifyContent: "flex-end",
            position: "sticky",
            bottom: -1,
            backgroundColor: "#fff",
            padding: "16px"
          }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSubmitting}
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
    </Drawer>
  );
};
